import AppIconButton from "../form/AppIconButton";
import { CircularProgress, Stack } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useTranslation } from "react-i18next";
import { AppAutocomplete } from "../form";
import { useIndexDateFieldOptions } from "../../hooks/useIndexDateFieldOptions";
import { resolveIndexDateFieldOption } from "../../utils/mappingDateFields";
import type { SxProps, Theme } from "@mui/material/styles";

type Props = {
  index: string;
  value?: string;
  onChange: (timeField: string) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  sx?: SxProps<Theme>;
  showReload?: boolean;
};

export default function IndexTimeFieldAutocomplete({
  index,
  value,
  onChange,
  disabled,
  label,
  helperText,
  sx,
  showReload = false,
}: Props) {
  const { t } = useTranslation();
  const { options, loading, error, reload } = useIndexDateFieldOptions(index);
  const selected = resolveIndexDateFieldOption(value, options);

  const resolvedHelper =
    error ||
    helperText ||
    (options.length === 0 && !loading && index.trim()
      ? t("widget.timeFieldNoDateFields")
      : undefined);

  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <AppAutocomplete
        sx={{ flex: 1, ...sx }}
        label={label ?? t("widget.timeField")}
        options={options}
        value={selected ?? undefined}
        onChange={(_, option) => onChange(option?.path ?? "")}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.path === b.path}
        loading={loading}
        disabled={disabled || !index.trim()}
        helperText={resolvedHelper}
        disableClearable
        noOptionsText={t("widget.timeFieldNoDateFields")}
      />
      {showReload && (
        <AppIconButton
          size="small"
          onClick={() => reload().catch(console.error)}
          disabled={disabled || loading || !index.trim()}
          aria-label={t("visualEditor.dslProbeRefresh")}
          sx={{ mt: 0.75 }}
        >
          {loading ? <CircularProgress size={18} /> : <RefreshOutlinedIcon fontSize="small" />}
        </AppIconButton>
      )}
    </Stack>
  );
}
